import Markdown from "react-markdown";
import { NOTE_CLASS } from "../ui/card";
import { FIELD_LABEL_CLASS, SECTION_HEADER_CLASS } from "../ui/section-header";
import type {
  JournalEntry,
  JournalNpcData,
  JournalLocationData,
  JournalQuestData,
  JournalFactionData,
  JournalHandoutData,
  JournalLoreData,
  JournalLink,
  JournalEntryType,
} from "@roll1/shared";
import { Link2 } from "lucide-react";
import { Pill } from "../ui/pill";
import { JOURNAL_TYPE_ICONS } from "../../lib/journal-utils";
import QuestObjectiveList from "./QuestObjectiveList";
import { useI18n } from "../../i18n";
import type { TranslationKeys } from "../../i18n";

type TFn = (key: keyof TranslationKeys) => string;

interface JournalEntryViewProps {
  entry: JournalEntry;
  entries: JournalEntry[];
  isGM: boolean;
  onNavigateToEntry?: (entryId: string) => void;
  /** The host shows the links itself (Studio: an editable «Links» section) — no read-only copy. */
  hideLinks?: boolean;
  /** Studio shows the quest's status and giver in its own card header; the view keeps to the objectives. */
  studioQuestCard?: boolean;
}

const MD_CLASS = "prose prose-invert prose-sm max-w-none text-fg";

function isFieldVisible(
  fieldKey: string,
  revealedFields: string[] | undefined,
  isGM: boolean,
): boolean {
  if (isGM) return true;
  if (revealedFields === undefined) return true;
  return revealedFields.includes(fieldKey);
}

function MdField({ content }: { content: string | undefined }) {
  if (!content) return null;
  return (
    <div className={MD_CLASS}>
      <Markdown>{content}</Markdown>
    </div>
  );
}

function GmSection({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className={`${NOTE_CLASS} mt-3`}>
      <span className={FIELD_LABEL_CLASS}>{label}</span>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function ImageBlock({ src }: { src: string | undefined }) {
  if (!src) return null;
  return <img src={src} alt="" className="max-w-full rounded mb-3" />;
}

function MetaItem({
  label,
  value,
}: {
  label: string;
  value: string | undefined;
}) {
  if (!value) return null;
  return (
    <span className="text-meta text-muted-foreground">
      {label}: <span className="text-fg">{value}</span>
    </span>
  );
}

// --- Type-specific views ---

function NpcView({
  data,
  isGM,
  t,
  revealedFields,
}: {
  data: JournalNpcData;
  isGM: boolean;
  t: TFn;
  revealedFields?: string[];
}) {
  return (
    <>
      {isFieldVisible("portrait", revealedFields, isGM) && (
        <ImageBlock src={data.portrait} />
      )}

      {/* Meta row: race · occupation · disposition */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-3">
        {isFieldVisible("race", revealedFields, isGM) && (
          <MetaItem label={t("journal.preview.race")} value={data.race} />
        )}
        {isFieldVisible("occupation", revealedFields, isGM) && (
          <MetaItem
            label={t("journal.preview.occupation")}
            value={data.occupation}
          />
        )}
        {isFieldVisible("disposition", revealedFields, isGM) && (
          <Pill tone="plain">{t(`journal.disposition.${data.disposition}`)}</Pill>
        )}
        {isGM && (
          <Pill tone={data.alive ? "plain" : "danger"}>
            {data.alive ? t("journal.preview.alive") : t("journal.preview.dead")}
          </Pill>
        )}
      </div>

      {isFieldVisible("description", revealedFields, isGM) && (
        <MdField content={data.description} />
      )}

      {isGM && data.personality && (
        <GmSection label={t("journal.preview.personality")}>
          <MdField content={data.personality} />
        </GmSection>
      )}

      {isGM && data.secrets && (
        <GmSection label={t("journal.preview.secrets")}>
          <MdField content={data.secrets} />
        </GmSection>
      )}

      {/* AI-созданные записи (journal-тулы, импорт) пишут data частично —
          массивы могут отсутствовать; формы стола их всегда сеют. */}
      {isGM && (data.questHooks?.length ?? 0) > 0 && (
        <GmSection label={t("journal.preview.questHooks")}>
          <ul className="list-disc list-inside text-ui text-fg">
            {(data.questHooks ?? []).map((h, i) => (
              <li key={i}>{h}</li>
            ))}
          </ul>
        </GmSection>
      )}
    </>
  );
}

function LocationView({
  data,
  isGM,
  t,
  revealedFields,
  entries,
  onNavigateToEntry,
}: {
  data: JournalLocationData;
  isGM: boolean;
  t: TFn;
  revealedFields?: string[];
  entries: JournalEntry[];
  onNavigateToEntry?: (id: string) => void;
}) {
  const parentEntry = data.parentEntryId
    ? entries.find((e) => e.id === data.parentEntryId)
    : undefined;

  return (
    <>
      {isFieldVisible("image", revealedFields, isGM) && (
        <ImageBlock src={data.image} />
      )}

      {parentEntry && (
        <div className="mb-3">
          <span className="text-meta text-dim">
            {t("journal.form.location.parent")}:{" "}
          </span>
          <button
            className="text-meta text-hl hover:underline"
            onClick={() => onNavigateToEntry?.(parentEntry.id)}
          >
            {parentEntry.name}
          </button>
        </div>
      )}

      {isFieldVisible("description", revealedFields, isGM) && (
        <MdField content={data.description} />
      )}

      {isGM && data.secrets && (
        <GmSection label={t("journal.preview.secrets")}>
          <MdField content={data.secrets} />
        </GmSection>
      )}
    </>
  );
}

function QuestView({
  entryId,
  data,
  isGM,
  t,
  revealedFields,
  entries,
  onNavigateToEntry,
  studioCard = false,
}: {
  studioCard?: boolean;
  entryId: string;
  data: JournalQuestData;
  isGM: boolean;
  t: TFn;
  revealedFields?: string[];
  entries: JournalEntry[];
  onNavigateToEntry?: (id: string) => void;
}) {
  const questGiver = data.questGiverEntryId
    ? entries.find((e) => e.id === data.questGiverEntryId)
    : undefined;
  // data.objectives может отсутствовать у AI-созданных квестов — см. questHooks.
  const visibleObjectives = isGM
    ? (data.objectives ?? [])
    : (data.objectives ?? []).filter((o) => !o.hidden);

  return (
    <>
      {/* Status + quest giver */}
      {!studioCard && (
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Pill tone="plain">{t(`journal.form.questStatus.${data.status}`)}</Pill>
        {questGiver && (
          <span className="text-meta text-muted-foreground">
            {t("journal.relation.quest_giver")}:{" "}
            <button
              className="text-hl hover:underline"
              onClick={() => onNavigateToEntry?.(questGiver.id)}
            >
              {questGiver.name}
            </button>
          </span>
        )}
      </div>
      )}

      {isFieldVisible("description", revealedFields, isGM) && (
        studioCard && data.description ? (
          <div className="border-b border-line px-3 py-2 text-ui text-muted-foreground"><MdField content={data.description} /></div>
        ) : (
          <MdField content={data.description} />
        )
      )}

      {isFieldVisible("objectives", revealedFields, isGM) &&
        visibleObjectives.length > 0 && (
          studioCard ? (
            // The objectives are the card's body (mockup quests__quest): no label, no box of their own.
            <QuestObjectiveList entryId={entryId} objectives={visibleObjectives} isGM={isGM} divided />
          ) : (
          <div className="mt-3">
            <span className={SECTION_HEADER_CLASS}>
              {t("journal.preview.objectives")}
            </span>
            {/* The GM ticks an objective right here, without editing the entry. */}
            <QuestObjectiveList entryId={entryId} objectives={visibleObjectives} isGM={isGM} />
          </div>
          )
        )}

      {isFieldVisible("rewards", revealedFields, isGM) && data.rewards && (
        <div className="mt-3">
          <span className={SECTION_HEADER_CLASS}>
            {t("journal.preview.rewards")}
          </span>
          <MdField content={data.rewards} />
        </div>
      )}

      {isGM && data.gmNotes && (
        <GmSection label={t("journal.preview.gmNotes")}>
          <MdField content={data.gmNotes} />
        </GmSection>
      )}
    </>
  );
}

function FactionView({
  data,
  isGM,
  t,
  revealedFields,
}: {
  data: JournalFactionData;
  isGM: boolean;
  t: TFn;
  revealedFields?: string[];
}) {
  return (
    <>
      {isFieldVisible("description", revealedFields, isGM) && (
        <MdField content={data.description} />
      )}
      {isFieldVisible("attitude", revealedFields, isGM) && data.attitude && (
        <div className="mt-2">
          <MetaItem
            label={t("journal.preview.attitude")}
            value={data.attitude}
          />
        </div>
      )}
      {isGM && data.secrets && (
        <GmSection label={t("journal.preview.secrets")}>
          <MdField content={data.secrets} />
        </GmSection>
      )}
    </>
  );
}

function HandoutView({
  data,
  isGM,
  t,
  revealedFields,
}: {
  data: JournalHandoutData;
  isGM: boolean;
  t: TFn;
  revealedFields?: string[];
}) {
  return (
    <>
      {isFieldVisible("image", revealedFields, isGM) && (
        <ImageBlock src={data.image} />
      )}
      {isFieldVisible("content", revealedFields, isGM) && (
        <MdField content={data.content} />
      )}
      {isGM && data.gmNotes && (
        <GmSection label={t("journal.preview.gmNotes")}>
          <MdField content={data.gmNotes} />
        </GmSection>
      )}
    </>
  );
}

function LoreView({
  data,
  isGM,
  t,
  revealedFields,
}: {
  data: JournalLoreData;
  isGM: boolean;
  t: TFn;
  revealedFields?: string[];
}) {
  return (
    <>
      {isFieldVisible("category", revealedFields, isGM) && data.category && (
        <div className="mb-3">
          <Pill tone="plain">{data.category}</Pill>
        </div>
      )}
      {isFieldVisible("content", revealedFields, isGM) && (
        <MdField content={data.content} />
      )}
      {isGM && data.gmNotes && (
        <div className="mt-3 border-t border-line pt-2">
          <div className={`mb-1 ${FIELD_LABEL_CLASS}`}>{t("journal.form.lore.gmNotes")}</div>
          <MdField content={data.gmNotes} />
        </div>
      )}
    </>
  );
}

// --- Read-only links ---

function ReadOnlyLinks({
  linksFrom,
  linksTo,
  entries,
  onNavigateToEntry,
  t,
}: {
  linksFrom: JournalLink[];
  linksTo: JournalLink[];
  entries: JournalEntry[];
  onNavigateToEntry?: (id: string) => void;
  t: TFn;
}) {
  if (linksFrom.length === 0 && linksTo.length === 0) return null;

  const getEntryName = (id: string) =>
    entries.find((e) => e.id === id)?.name ?? id;
  const getEntryType = (id: string): JournalEntryType | undefined =>
    entries.find((e) => e.id === id)?.type;
  const getRelationLabel = (relation: string) => {
    const key = `journal.relation.${relation}` as keyof TranslationKeys;
    const result = t(key);
    return result !== key ? result : relation;
  };

  return (
    <div className="space-y-1.5 pt-3 mt-3 border-t border-line">
      <span className={`${SECTION_HEADER_CLASS} flex items-center gap-1`}>
        <Link2 size={14} />
        {t("journal.links.title")}
      </span>

      {linksFrom.map((link) => {
        const targetType = getEntryType(link.targetId);
        const TargetIcon = targetType
          ? JOURNAL_TYPE_ICONS[targetType]
          : undefined;
        return (
          <div
            key={`${link.targetId}-${link.relation}`}
            className="flex items-center gap-1 text-meta"
          >
            <span className="text-dim shrink-0">
              {getRelationLabel(link.relation)}
            </span>
            <span className="text-dim shrink-0">&rarr;</span>
            {TargetIcon && (
              <TargetIcon size={12} className="shrink-0 text-muted-foreground" />
            )}
            <button
              className="text-hl hover:underline truncate text-left"
              onClick={() => onNavigateToEntry?.(link.targetId)}
            >
              {getEntryName(link.targetId)}
            </button>
          </div>
        );
      })}

      {linksTo.length > 0 && (
        <>
          <span className="text-label font-semibold uppercase tracking-label text-dim mt-2 block">
            {t("journal.links.incoming")}
          </span>
          {linksTo.map((link) => {
            const sourceType = getEntryType(link.sourceId);
            const SourceIcon = sourceType
              ? JOURNAL_TYPE_ICONS[sourceType]
              : undefined;
            return (
              <div
                key={`${link.sourceId}-${link.relation}`}
                className="flex items-center gap-1 text-meta"
              >
                {SourceIcon && (
                  <SourceIcon size={12} className="shrink-0 text-muted-foreground" />
                )}
                <button
                  className="text-hl hover:underline truncate text-left"
                  onClick={() => onNavigateToEntry?.(link.sourceId)}
                >
                  {getEntryName(link.sourceId)}
                </button>
                <span className="text-dim shrink-0">&rarr;</span>
                <span className="text-dim shrink-0">
                  {getRelationLabel(link.relation)}
                </span>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}

// --- Main component ---

export default function JournalEntryView({
  entry,
  entries,
  isGM,
  onNavigateToEntry,
  hideLinks = false,
  studioQuestCard = false,
}: JournalEntryViewProps) {
  const { t } = useI18n();
  const { data, revealedFields } = entry;

  const renderContent = () => {
    switch (entry.type) {
      case "npc":
        return (
          <NpcView
            data={data as JournalNpcData}
            isGM={isGM}
            t={t}
            revealedFields={revealedFields}
          />
        );
      case "location":
        return (
          <LocationView
            data={data as JournalLocationData}
            isGM={isGM}
            t={t}
            revealedFields={revealedFields}
            entries={entries}
            onNavigateToEntry={onNavigateToEntry}
          />
        );
      case "quest":
        return (
          <QuestView
            entryId={entry.id}
            data={data as JournalQuestData}
            isGM={isGM}
            t={t}
            revealedFields={revealedFields}
            entries={entries}
            onNavigateToEntry={onNavigateToEntry}
            studioCard={studioQuestCard}
          />
        );
      case "faction":
        return (
          <FactionView
            data={data as JournalFactionData}
            isGM={isGM}
            t={t}
            revealedFields={revealedFields}
          />
        );
      case "handout":
        return (
          <HandoutView
            data={data as JournalHandoutData}
            isGM={isGM}
            t={t}
            revealedFields={revealedFields}
          />
        );
      case "lore":
        return (
          <LoreView
            data={data as JournalLoreData}
            isGM={isGM}
            t={t}
            revealedFields={revealedFields}
          />
        );
      default:
        return null;
    }
  };

  const content = renderContent();
  const hasContent =
    content !== null &&
    Object.values(data).some(
      (v) =>
        (typeof v === "string" && v.trim()) ||
        (Array.isArray(v) && v.length > 0),
    );

  return (
    <div>
      {hasContent ? (
        content
      ) : (
        <p className="text-ui text-dim">
          {t("journal.editor.noContent")}
        </p>
      )}

      {hideLinks ? null : (
        <ReadOnlyLinks
          linksFrom={entry.linksFrom ?? []}
          linksTo={entry.linksTo ?? []}
          entries={entries}
          onNavigateToEntry={onNavigateToEntry}
          t={t}
        />
      )}
    </div>
  );
}
